import inquirer from "inquirer"

import { splitCsv } from "../../helpers.js"
import { SetupContext } from "../../flow/types.js"
import { colorize, emphasis, muted, renderFramedLines } from "../../ui.js"
import { DependencyMode } from "../runtime.js"
import { PRIVATE_DEPENDENCIES_DOCS_URL } from "../../constants.js"

export async function promptDependencyPackageNames(
  context: SetupContext,
  error: (message: string) => never,
  log: (message: string) => void,
): Promise<string[]> {
  if (context.componentPackages?.length) {
    log(muted(`Discovered packages: ${context.componentPackages.join(", ")}`))
  }

  const response = await inquirer.prompt<{ packageNames: string }>([
    {
      default: context.dependencyPackageNames?.join(", ") ?? context.componentPackages?.join(", ") ?? "",
      message: colorize("Which design system packages should we add? (comma-separated)", "blue"),
      name: "packageNames",
      type: "input",
    },
  ])

  const packageNames = splitCsv(response.packageNames)
  if (packageNames.length === 0) {
    error("At least one design system package is required.")
  }

  const invalid = packageNames.find(name => name.endsWith("/*"))
  if (invalid) {
    error(`Package "${invalid}" must be an exact package name.`)
  }

  return packageNames
}

export async function promptPrivateRegistryConfiguration(input: {
  context: SetupContext
  ensureLocalNpmrcFile: (templateDestinationPath: string) => Promise<string>
  error: (message: string) => never
  log: (message: string) => void
}): Promise<{ dependencyMode: "Public" | "Private"; npmToken?: string }> {
  const modeResponse = await inquirer.prompt<{ dependencyMode: DependencyMode }>([
    {
      choices: [
        { name: "Packages are private requiring a registry access token", value: "Private" },
        { name: "Packages are public", value: "Public" },
      ],
      default: input.context.dependencyMode ?? "Private",
      message: colorize("Are your packages private or public?", "blue"),
      name: "dependencyMode",
      type: "list",
    },
  ])

  if (modeResponse.dependencyMode === "Public") {
    return { dependencyMode: "Public" }
  }

  if (!input.context.templateDestinationPath) {
    input.error("Template destination path is missing.")
  }

  const npmrcPath = await input.ensureLocalNpmrcFile(input.context.templateDestinationPath)

  input.log(
    renderFramedLines([
      "Private packages are installed during the Docker build with an npm token passed as a Docker secret.",
      "The token is kept in memory for this setup run, passed to template upload, and not saved to the template or setup session.",
      "",
      `${emphasis(".npmrc uses:")} //registry.npmjs.org/:_authToken=\${NPM_TOKEN}`,
      `${emphasis(".npmrc path:")} ${npmrcPath}`,
      "",
      `${emphasis("📘 Docs:")} ${PRIVATE_DEPENDENCIES_DOCS_URL}`,
    ]),
  )

  const npmToken = await promptNpmToken(input.error)

  return {
    dependencyMode: "Private",
    npmToken,
  }
}

export async function promptNpmToken(error: (message: string) => never): Promise<string> {
  const tokenResponse = await inquirer.prompt<{ npmToken: string }>([
    {
      mask: "*",
      message: colorize("NPM token:", "blue"),
      name: "npmToken",
      type: "password",
      validate(value: string) {
        return value.trim().length > 0 || "NPM token is required for private packages."
      },
    },
  ])

  const npmToken = tokenResponse.npmToken.trim()
  if (!npmToken) {
    error("NPM token is required for private packages.")
  }

  return npmToken
}
